/** A retype report in words (deliverable 17), pure: fixed phrasing, labels from the types. */
import type { DocTypeV1, RetypeReportV1 } from '@codex/contracts';

export interface RetypeLine {
  readonly key: string;
  readonly text: string;
}

const CAPABILITY_WORDS: Readonly<Record<string, string>> = {
  speaker: 'speaking lines',
  place: 'being a place',
  holder: 'holding items',
  has_sheet: 'a character sheet',
  structural: 'a place in the outline',
  playable: 'play state',
  discoverable: 'discovery state',
  session: 'session tools',
  has_card: 'a card',
};

const word = (capability: string) => CAPABILITY_WORDS[capability] ?? capability.replace(/_/g, ' ');
const count = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

function labelOf(types: readonly DocTypeV1[], fieldDefId: string | null): string {
  if (fieldDefId === null) return 'a field';
  for (const type of types) {
    const found = type.fields.find((field) => field.id === fieldDefId);
    if (found) return found.label;
  }
  return 'a field';
}

/** Each field's outcome, then links, placements, states and capabilities, one line apiece. */
export function describeRetype(
  report: RetypeReportV1,
  types: readonly DocTypeV1[],
): { fields: RetypeLine[]; summary: RetypeLine[] } {
  const fields = report.fields.map((field): RetypeLine => {
    const label = labelOf(types, field.fieldDefId);
    const text =
      field.outcome === 'kept'
        ? `${label}: kept`
        : field.outcome === 'mapped'
          ? `${label}: moved to ${labelOf(types, field.toFieldDefId)}`
          : `${label}: set aside`;
    return { key: field.fieldDefId, text };
  });
  const summary: RetypeLine[] = [];
  const moved = (key: string, noun: string, m: { repointed: number; detached: number }) => {
    if (m.repointed + m.detached === 0) return;
    const parts = [];
    if (m.repointed > 0) parts.push(`${count(m.repointed, noun, `${noun}s`)} re-pointed`);
    if (m.detached > 0) parts.push(`${count(m.detached, noun, `${noun}s`)} detached`);
    summary.push({ key, text: parts.join(', ') });
  };
  moved('links', 'link', report.links);
  moved('placements', 'placement', report.placements);
  if (report.statesRepointed > 0)
    summary.push({
      key: 'states-repointed',
      text: `${count(report.statesRepointed, 'dated value', 'dated values')} re-pointed`,
    });
  if (report.statesKept > 0)
    summary.push({
      key: 'states-kept',
      text: `${count(report.statesKept, 'dated value', 'dated values')} kept and hidden`,
    });
  if (report.capabilities.lost.length > 0)
    summary.push({
      key: 'lost',
      text: `Loses ${report.capabilities.lost.map(word).join(', ')}`,
    });
  if (report.capabilities.gained.length > 0)
    summary.push({
      key: 'gained',
      text: `Gains ${report.capabilities.gained.map(word).join(', ')}`,
    });
  return { fields, summary };
}
